import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { BsBoxArrowUpRight } from '@react-icons/all-files/bs/BsBoxArrowUpRight';
import { matchPath, RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownMenu, DropdownToggle, UncontrolledButtonDropdown } from 'reactstrap';
import { bind } from '../Utils/Bind';

export interface ISidebarLinkModel {
    label: string;
    icon?: string;
    url: string;
    subLinks?: ISidebarLinkModel[];
}

export interface ISidebarLinkProps extends RouteComponentProps<undefined> {
    onClick?(): void;
    readonly label: string;
    readonly icon?: string;
    readonly customIcon?: React.ReactNode;
    readonly url: string;
    readonly subLink?: boolean;
    readonly sidebarCollapsed?: boolean;
    readonly exact?: boolean;
    readonly strict?: boolean;
    readonly child?: boolean;
    readonly externalLink?: boolean;
    readonly disableLink?: boolean;
    readonly linkNotificationCount?: number;
}

@observer
export class SidebarLink extends React.Component<ISidebarLinkProps> {
    @action
    public componentDidMount() {
        if (this.isMatch) {
            this.collapsed = false;
        }

        document.addEventListener('mousedown', this.handleClickOutsideChildren);
    }

    public componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutsideChildren);
    }

    public render() {
        const {
            child,
            sidebarCollapsed,
            externalLink,
            exact,
            children,
            strict,
            url,
            onClick,
            icon,
            customIcon,
            label,
            disableLink,
        } = this.props;
        return (
            <>
                {sidebarCollapsed && children && this.renderDropdown()}

                {sidebarCollapsed && !children && !child && !disableLink && (
                    <LinkContainer exact={exact} strict={strict} to={url} onClick={onClick}>
                        <div className="sidebar-link-no-child">{this.renderDropdown()}</div>
                    </LinkContainer>
                )}

                {!sidebarCollapsed && !externalLink && !children && (
                    <LinkContainer exact={exact} strict={strict} to={url} onClick={onClick}>
                        <li className={sidebarCollapsed ? 'sidebar-link collapsed' : 'sidebar-link'}>
                            <a className="nav-item sidebar-item" href={url}>
                                {!customIcon && <em className={'nav-icon '} />}
                                {customIcon}

                                {label}
                            </a>
                        </li>
                    </LinkContainer>
                )}

                {child && sidebarCollapsed && !externalLink && (
                    <LinkContainer exact={exact} strict={strict} to={url} onClick={onClick}>
                        <li className="sidebar-link">
                            <a
                                className={
                                    child && sidebarCollapsed
                                        ? 'nav-item sidebar-item text-center'
                                        : 'nav-item sidebar-item'
                                }
                                href={url}
                            >
                                {!child && icon && <em className={'nav-icon fa ' + icon} />}
                                {customIcon}
                                {child && !sidebarCollapsed && <em className={'nav-icon'} />}

                                {label}
                            </a>
                        </li>
                    </LinkContainer>
                )}

                {!children && externalLink && (
                    <li
                        className="sidebar-link"
                        onMouseEnter={this.toggleExternalLinkIcon}
                        onMouseLeave={this.toggleExternalLinkIcon}
                    >
                        <a
                            className={
                                child && sidebarCollapsed
                                    ? 'nav-item sidebar-item text-center'
                                    : 'nav-item sidebar-item'
                            }
                            href={url}
                            onClick={this.onClick}
                            target="_blank"
                        >
                            {!child && icon && <em className={'nav-icon'} />}
                            {customIcon}
                            {child && !sidebarCollapsed && <em className={'nav-icon'} />}

                            {label}

                            {this.showExternalLinkIcon && <BsBoxArrowUpRight className="sidebar-external-link p-3" />}
                        </a>
                    </li>
                )}

                {children && !sidebarCollapsed && (
                    <li className={this.parentClass}>
                        <a className=" nav-item sidebar-item" onClick={this.onToggle}>
                            {!customIcon && <em className={'nav-icon'} />}
                            {customIcon}

                            {label}

                            <em className="py-3 float-end" />
                        </a>
                    </li>
                )}

                {children && !sidebarCollapsed && (
                    <ul ref={this.bindChildrenLinks} className={this.childLinksClass}>
                        {children}
                    </ul>
                )}
            </>
        );
    }

    @bind
    private renderDropdown() {
        const { exact, children, strict, url, onClick, icon, customIcon, label } = this.props;

        return (
            <UncontrolledButtonDropdown
                direction="end"
                className={this.buttonClass}
                isOpen={!this.dropdownClosed}
                toggle={this.toggleDropdown}
                onMouseEnter={action(() => (this.dropdownClosed = false))}
                onMouseLeave={action(() => (this.dropdownClosed = true))}
            >
                <DropdownToggle className="sidebar-dropdown-button" color="clear">
                    {customIcon}
                    {!customIcon && <em className={'sidebar-dropdown-icon fa ' + icon} />}
                </DropdownToggle>

                <DropdownMenu className="sidebar-dropdown-menu">
                    <ul>
                        {!children && (
                            <LinkContainer exact={exact} strict={strict} to={url} onClick={onClick}>
                                <li className="sidebar-link header-no-children">
                                    <a className="nav-item sidebar-item text-center" href={url}>
                                        {label}
                                    </a>
                                </li>
                            </LinkContainer>
                        )}

                        {children && (
                            <li className="sidebar-link header-children">
                                <div className="header-children nav-item sidebar-item text-center">{label}</div>
                            </li>
                        )}

                        {children}
                    </ul>
                </DropdownMenu>
            </UncontrolledButtonDropdown>
        );
    }

    @bind
    @action
    private bindChildrenLinks(element: HTMLUListElement | null) {
        this.childrenLinksElement = element;
    }

    @bind
    @action
    private handleClickOutsideChildren(event: MouseEvent) {
        const element = event.target as HTMLElement;

        const isAnotherLink = element?.innerText != this.props.label && element?.classList.contains('sidebar-item');
        if (!this.childrenLinksElement?.contains(element) && isAnotherLink) {
            this.collapsed = true;
        }
    }

    @bind
    private onClick(event: React.MouseEvent<HTMLAnchorElement>) {
        if (this.props.onClick) {
            this.props.onClick();
        }
        this.toggleDropdown();
    }

    @computed
    private get childLinksClass() {
        if (this.collapsed) {
            return 'slide-out';
        }

        if (this.props.children && this.isMatch) {
            return 'sidebar-link slide-in child-selected';
        }

        return 'sidebar-link slide-in';
    }

    @computed
    private get buttonClass() {
        let className = 'sidebar-dropdown me-auto text-center float-start';

        if (this.isMatch || !this.dropdownClosed) className += ' active'; // TODO sidebar-dropdown does not respond to active

        return className;
    }

    @computed
    private get parentClass() {
        let className = 'sidebar-link';

        if (this.isMatch && this.props.children && this.collapsed) className += ' active';

        if (this.isMatch && this.props.children && !this.collapsed) className += ' child-selected';

        return className;
    }

    @computed
    private get isMatch() {
        return !!matchPath(this.props.location.pathname, {
            path: this.props.url,
            exact: this.props.exact,
            strict: this.props.strict,
        });
    }

    @bind
    @action
    private toggleExternalLinkIcon() {
        this.showExternalLinkIcon = !this.showExternalLinkIcon;
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @bind
    @action
    private toggleDropdown() {
        this.dropdownClosed = !this.dropdownClosed;
    }

    @observable
    private dropdownClosed = true;
    @observable
    private collapsed = true;
    @observable
    private showExternalLinkIcon = false;
    private childrenLinksElement: HTMLUListElement | null = null;
}
